import Cookies from "js-cookie";
import { create } from "zustand";
import { logAPI } from "@/services/logger";
import { AUTH_COOKIE_NAME } from "@knitknot/shared/constants";

// Refresh token constants
const REFRESH_TOKEN_KEY = "knitknot_refresh_token";

/**
 * Bytes so far, and the size to measure them against.
 *
 * `totalBytes` is null when the server sent neither a `Content-Length` nor an
 * estimate — the caller must then render an indeterminate state rather than
 * inventing a denominator.
 */
export interface DownloadProgress {
  receivedBytes: number;
  totalBytes: number | null;
}

export interface DownloadFileOptions {
  /** Called on every chunk. Its presence is what switches on stream reading. */
  onProgress?: (progress: DownloadProgress) => void;
  /** Abort the transfer — a long export needs a way out. */
  signal?: AbortSignal;
}

/** What actually landed on disk, for the "saved" confirmation. */
export interface DownloadResult {
  filename: string;
  bytes: number;
  /** The response headers, for callers whose endpoint rides a value on one
   *  (e.g. the Agent Mirror export's optional `x-knitknot-hmac-key-hex`). */
  headers: Headers;
}

/**
 * Extract FastAPI's `detail` string from an error thrown by ApiClient.
 * Errors are thrown as `Error("HTTP <status>: <raw response body>")`; the
 * body is usually `{"detail": "..."}`. Falls back to `undefined` when the
 * error isn't shaped that way (network failure, non-JSON body, etc.) so
 * callers can supply their own generic message.
 */
export function getApiErrorDetail(error: unknown): string | undefined {
  if (!(error instanceof Error)) return undefined;
  const match = error.message.match(/^HTTP \d+: (.*)$/s);
  if (!match) return undefined;
  try {
    const body = JSON.parse(match[1]);
    return typeof body?.detail === "string" ? body.detail : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Extract the HTTP status from an error thrown by ApiClient.
 * Returns `undefined` for anything that never reached a response — a dropped
 * connection, DNS failure, CORS block or an aborted request all throw a bare
 * `TypeError`/`Error` with no `HTTP <status>:` prefix.
 */
export function getApiErrorStatus(error: unknown): number | undefined {
  if (!(error instanceof Error)) return undefined;
  const match = error.message.match(/^HTTP (\d{3}):/);
  return match ? Number(match[1]) : undefined;
}

/**
 * Rule 59's discriminator: did the record genuinely not exist, or did the fetch
 * fail? Only a 404/410 means "this thing is gone" (→ `EmptyState`). Everything
 * else — a 5xx, a 429, a timeout, a dropped connection — is a transient failure
 * the user can retry (→ `ErrorState` with `onRetry` wired to `refetch`).
 * Collapsing the two is how a flaky network ends up reading as "doesn't exist".
 */
export function isNotFoundError(error: unknown): boolean {
  const status = getApiErrorStatus(error);
  return status === 404 || status === 410;
}

/**
 * Get the refresh token from cookies.
 */
const getRefreshToken = (): string | undefined => {
  return Cookies.get(REFRESH_TOKEN_KEY);
};

/**
 * Clear all auth tokens from cookies.
 */
const clearAuthTokens = () => {
  Cookies.remove(AUTH_COOKIE_NAME, { path: '/' });
  Cookies.remove(REFRESH_TOKEN_KEY, { path: '/' });
};

// All request shapes share one refresh, including requests arriving mid-refresh.
let refreshPromise: Promise<string | null> | null = null;

/**
 * Attempt to refresh the access token using the refresh token.
 * Returns the new access token if successful, null otherwise.
 * Handles concurrent refresh requests by reusing the same promise.
 */
const refreshAccessToken = async (): Promise<string | null> => {
  // If already refreshing, wait for that to complete
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    console.warn('[Auth] No refresh token available for token refresh');
    return null;
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${apiBasePath}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        console.error('[Auth] Token refresh failed:', response.status, response.statusText);
        return null;
      }

      const data = await response.json();
      if (data.token) {
        // Match the cookie configuration from authService.ts
        Cookies.set(AUTH_COOKIE_NAME, data.token, {
          sameSite: 'lax',
          secure: import.meta.env.PROD,
          path: '/',
          expires: 1 // 1 day (match 24h access token)
        });
        return data.token;
      }
      return null;
    } catch (error) {
      console.error('[Auth] Token refresh request failed:', error);
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Handle 401 by attempting token refresh first, then redirect if refresh fails.
 * Returns true if refresh succeeded and request should be retried.
 */
const handleUnauthorizedWithRefresh = async (rejectedToken?: string): Promise<boolean> => {
  // A delayed 401 may belong to a token another request already replaced.
  // Retry that newer token once instead of exchanging the refresh token again.
  const currentToken = Cookies.get(AUTH_COOKIE_NAME);
  const newToken = currentToken && currentToken !== rejectedToken
    ? currentToken
    : await refreshAccessToken();

  if (newToken) {
    return true; // Refresh succeeded, retry the request
  }

  // Refresh failed, clear tokens and redirect to login
  clearAuthTokens();
  if (!window.location.pathname.includes('/signin') && !window.location.pathname.includes('/callback')) {
    window.location.href = '/signin';
  }
  return false;
};

/** This is only a scheduling hint; the server still verifies every JWT. */
const isExpiredAccessToken = (token: string | undefined): boolean => {
  if (!token) return false;
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, '=')));
    // Let the server decide marginal expiry: the device clock may run ahead.
    // This does not extend token validity; a server 401 still refreshes/retries.
    return typeof exp === 'number' && Number.isFinite(exp) && exp * 1000 + 30_000 <= Date.now();
  } catch {
    // Opaque/malformed tokens retain the existing server rejection path.
    return false;
  }
};

const requestWithAuth = async (
  makeRequest: (token: string | undefined) => Promise<Response>,
  signal?: AbortSignal | null,
): Promise<Response> => {
  // Cancelling one request must not cancel the refresh shared by other callers.
  const refresh = async (rejectedToken: string | undefined) => {
    signal?.throwIfAborted();
    let onAbort: (() => void) | undefined;
    try {
      const pending = handleUnauthorizedWithRefresh(rejectedToken);
      const refreshed = signal ? await Promise.race([
        pending,
        new Promise<never>((_, reject) => {
          onAbort = () => reject(signal.reason);
          signal.addEventListener('abort', onAbort, { once: true });
        }),
      ]) : await pending;
      if (!refreshed) throw new Error('Unauthorized');
    } finally {
      if (onAbort) signal?.removeEventListener('abort', onAbort);
    }
  };

  signal?.throwIfAborted();
  let token = Cookies.get(AUTH_COOKIE_NAME);
  if (refreshPromise || ((!token || isExpiredAccessToken(token)) && getRefreshToken())) {
    await refresh(token);
    token = Cookies.get(AUTH_COOKIE_NAME);
  }

  let response = await makeRequest(token);
  if (response.status === 401) {
    await refresh(token);
    response = await makeRequest(Cookies.get(AUTH_COOKIE_NAME));
  }
  return response;
};

// Base configuration
const getApiBasePath = () => {
  if (import.meta.env.MODE === 'development' || import.meta.env.DEV) {
    return "/api";
  }
  return import.meta.env.VITE_API_BASE_URL ?? "/api";
};

export const apiBasePath = getApiBasePath();
export const FRONTEND_PATH = encodeURIComponent(window.location.origin);

// Helper function to construct API endpoints correctly
const constructEndpoint = (path: string) => {
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${apiBasePath}${normalizedPath}`;
};

// API Endpoints
export const ApiEndpoints = {
  loginRedirect: constructEndpoint('/auth/login'),
  logout: constructEndpoint('/auth/logout'),
  authenticate: constructEndpoint('/auth/authenticate'),
};

// Loading state store
interface LoadingState {
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  homeLoading: boolean;
  setIsAuthenticating: (isAuthenticating: boolean) => void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setHomeLoading: (isLoading: boolean) => void;
}

export const useLoadingState = create<LoadingState>((set) => ({
  isAuthenticated: false,
  isAuthenticating: false,
  homeLoading: true,
  setIsAuthenticating: (isAuthenticating) => set({ isAuthenticating }),
  setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
  setHomeLoading: (homeLoading) => set({ homeLoading }),
}));

// Local Storage Utilities
export const getFromLocalStorage = (key: string) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch {
    return null;
  }
};

/**
 * Per-request options. `expectedStatuses` names the non-2xx statuses this
 * caller treats as a legitimate ANSWER rather than a failure — the canonical
 * case is an existence probe whose 404 means "no" (`useContentPage`). The
 * request still rejects, so nothing about a caller's control flow changes; the
 * status is simply not logged at ERROR. Without it a by-design 404 prints
 * `ERROR [api]` on every render, which breaks the "0 console errors" gate and
 * inflates any 4xx dashboard with expected traffic.
 */
type ApiRequestOptions = RequestInit & {
  params?: Record<string, string>;
  expectedStatuses?: number[];
};

// New simplified API client for services
class ApiClient {
  private baseUrl = apiBasePath;

  constructor() {
    // Remove verbose initialization log
  }

  private async request<T>(method: string, path: string, data?: unknown, options?: ApiRequestOptions): Promise<T> {
    const startTime = performance.now();

    // Statuses this caller EXPECTS, so they are not logged as errors. The
    // request still throws — callers branch on the thrown `HTTP {status}`
    // message — but a by-design 404 (an ownership probe that answers "no")
    // is not a failure and must not print `ERROR [api]` in the console.
    const { expectedStatuses, ...fetchOptions } = options ?? {};
    const isExpected = (status: number) => !!expectedStatuses?.includes(status);

    // Normalize path for API requests
    let normalizedPath = path;
    if (!path.startsWith("http")) {
      normalizedPath = path.startsWith('/') ? path : '/' + path;
    }

    // Construct the full URL
    let url = normalizedPath.startsWith("http") ? normalizedPath : `${this.baseUrl}${normalizedPath}`;

    // Add query parameters if provided
    if (fetchOptions.params) {
      const searchParams = new URLSearchParams(fetchOptions.params);
      url = `${url}?${searchParams.toString()}`;
    }

    // Log request
    logAPI.request(method, url);

    const makeRequest = async (authToken: string | undefined) => {
      // Body-less GET/HEAD requests never send a Content-Type — sending one
      // anyway turns every such request into a CORS preflight (an extra
      // OPTIONS round-trip per unique URL). Keep it for every request that
      // actually carries a body.
      const isBodyless = method === "GET" || method === "HEAD";
      const headers: Record<string, string> = {
        ...(isBodyless ? {} : { "Content-Type": "application/json" }),
        ...(fetchOptions.headers as Record<string, string> | undefined),
      };

      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      }

      return fetch(url, {
        method,
        headers,
        body: data ? JSON.stringify(data) : undefined,
        credentials: "include",
        ...fetchOptions,
      });
    };

    try {
      const response = await requestWithAuth(makeRequest, fetchOptions.signal);

      const duration = Math.round(performance.now() - startTime);
      logAPI.response(method, url, response.status, duration, {}, isExpected(response.status));

      if (!response.ok) {
        const errorText = await response.text();
        const error = new Error(`HTTP ${response.status}: ${errorText}`);
        if (!isExpected(response.status)) {
          logAPI.error(method, url, error);
        }
        throw error;
      }

      // Handle empty responses
      const text = await response.text();
      return text ? JSON.parse(text) : undefined;
    } catch (error) {
      if (error instanceof Error && !error.message.startsWith("HTTP ")) {
        logAPI.error(method, url, error);
      }
      throw error;
    }
  }

  async get<T>(path: string, options?: { params?: Record<string, string>; expectedStatuses?: number[] }): Promise<T> {
    // 'no-cache' (not 'no-store'): the backend emits ETags, so this still
    // revalidates every request but lets the browser send If-None-Match and
    // reuse the cached body on a 304 instead of re-downloading the payload.
    return this.request<T>("GET", path, undefined, { ...options, cache: 'no-cache' });
  }

  /**
   * GET a plain-text body (an `llms.txt`, a Markdown export). `request()` ends
   * every call with `JSON.parse`, so text needs its own path; auth + the 401
   * refresh mirror `downloadFile`.
   */
  async getText(path: string, options?: { expectedStatuses?: number[] }): Promise<string> {
    const normalizedPath = path.startsWith("http") ? path : (path.startsWith("/") ? path : `/${path}`);
    const url = normalizedPath.startsWith("http") ? normalizedPath : `${this.baseUrl}${normalizedPath}`;
    const makeRequest = async (authToken: string | undefined) => {
      const headers: Record<string, string> = { Accept: "text/plain, text/markdown, */*" };
      if (authToken) headers.Authorization = `Bearer ${authToken}`;
      return fetch(url, { method: "GET", headers, credentials: "include", cache: "no-cache" });
    };
    const response = await requestWithAuth(makeRequest);
    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(`HTTP ${response.status}: ${errorText}`);
      if (!options?.expectedStatuses?.includes(response.status)) logAPI.error("GET", url, error);
      throw error;
    }
    return response.text();
  }

  async post<T>(
    path: string,
    data?: unknown,
    options?: { params?: Record<string, string>; signal?: AbortSignal },
  ): Promise<T> {
    return this.request<T>("POST", path, data, options);
  }

  async put<T>(path: string, data?: unknown): Promise<T> {
    return this.request<T>("PUT", path, data);
  }

  async patch<T>(path: string, data?: unknown): Promise<T> {
    return this.request<T>("PATCH", path, data);
  }

  async delete<T>(path: string, options?: { params?: Record<string, string> }): Promise<T> {
    return this.request<T>("DELETE", path, undefined, options);
  }

  async uploadFile<T>(path: string, formData: FormData): Promise<T> {
    const makeRequest = async (authToken: string | undefined) => {
      const headers: Record<string, string> = {};

      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      } else {
        console.warn("No auth token found for file upload");
      }

      // Normalize path for API requests - no /api prefix needed
      let normalizedPath = path;
      if (!path.startsWith("http")) {
        // For relative paths, ensure they start with /
        normalizedPath = path.startsWith('/') ? path : '/' + path;
      }

      // Construct the full URL
      const url = normalizedPath.startsWith("http") ? normalizedPath : `${this.baseUrl}${normalizedPath}`;

      return fetch(url, {
        method: "POST",
        headers,
        body: formData,
        credentials: "include",
      });
    };

    const response = await requestWithAuth(makeRequest);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    return response.json();
  }

  /**
   * Fetch a binary attachment and hand it to the browser as a download.
   *
   * `request()` cannot serve this: it ends every call with `JSON.parse(text)`,
   * which mangles a zip. This mirrors `uploadFile`'s auth + 401-refresh path
   * rather than a bare `fetch`, so a download started on a stale token
   * refreshes and retries like every other call instead of failing at the
   * one moment the user is waiting on a big file.
   *
   * The filename comes from the server's `Content-Disposition` — the backend
   * already names the artifact, and re-deriving it here would be a second
   * naming rule that drifts from the first.
   *
   * The body is READ AS A STREAM rather than `response.blob()`, so `onProgress`
   * can report bytes as they land. Big exports are built and compressed onto
   * the wire (the backend streams the zip), which means the byte count is real
   * work happening, not just a network transfer — it is the only signal the
   * user has that a 15-second wait is alive.
   */
  async downloadFile(
    path: string,
    fallbackFilename: string,
    options: DownloadFileOptions = {},
  ): Promise<DownloadResult> {
    const { onProgress, signal } = options;
    const normalizedPath = path.startsWith("http") ? path : (path.startsWith("/") ? path : `/${path}`);
    const url = normalizedPath.startsWith("http") ? normalizedPath : `${this.baseUrl}${normalizedPath}`;

    const makeRequest = async (authToken: string | undefined) => {
      const headers: Record<string, string> = {};
      if (authToken) headers.Authorization = `Bearer ${authToken}`;
      return fetch(url, { method: "GET", headers, credentials: "include", signal });
    };

    const response = await requestWithAuth(makeRequest, signal);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const disposition = response.headers.get("Content-Disposition") ?? "";
    const match = /filename\*?=(?:UTF-8'')?"?([^\";]+)"?/i.exec(disposition);
    const filename = match ? decodeURIComponent(match[1]) : fallbackFilename;

    // A streamed response has no Content-Length, so the export endpoint sends
    // its own estimate. Content-Length wins where it exists — it is exact.
    const declaredTotal = Number(response.headers.get("Content-Length"));
    const estimatedTotal = Number(response.headers.get("X-Export-Estimated-Bytes"));
    const totalBytes = declaredTotal > 0 ? declaredTotal : estimatedTotal > 0 ? estimatedTotal : null;

    const blob = await readBodyWithProgress(response, { totalBytes, onProgress });

    const objectUrl = URL.createObjectURL(blob);
    try {
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } finally {
      // Revoke on the next tick, not synchronously: Safari aborts a download
      // whose object URL is revoked in the same task as the click.
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    }

    return { filename, bytes: blob.size, headers: response.headers };
  }
}

/**
 * Drain a response body, reporting bytes as they arrive.
 *
 * Falls back to `response.blob()` where the stream is not readable — the
 * download still works, it just cannot report progress, which is why
 * `receivedBytes` is only ever reported from real chunks and never faked at
 * the halfway point.
 */
async function readBodyWithProgress(
  response: Response,
  { totalBytes, onProgress }: { totalBytes: number | null; onProgress?: (progress: DownloadProgress) => void },
): Promise<Blob> {
  const contentType = response.headers.get("Content-Type") ?? "application/octet-stream";
  if (!response.body || !onProgress) {
    return response.blob();
  }

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let receivedBytes = 0;

  onProgress({ receivedBytes: 0, totalBytes });
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    receivedBytes += value.byteLength;
    onProgress({ receivedBytes, totalBytes });
  }

  return new Blob(chunks as BlobPart[], { type: contentType });
}

export default new ApiClient();
